import { useMemo, useState } from 'react';
import { useUser } from '../context/UserContext';
import { consultarAccionesCampania, guardarAccionesCampania } from '../api/apiConfig';

const emptyFila = () => ({
  tipo_penetracion: '',
  accion_visita: '',
  accion_contacto: '',
  accion_oferta: '',
  oferta_detalle: '',
  accion_mail: '',
  desglose: [{ categoria: '', cantidad: '' }],
});

const AccionesCampana = () => {
  const { user } = useUser();
  const [nombreCam, setNombreCam] = useState('');
  const [fecIni, setFecIni] = useState('');
  const [fecFin, setFecFin] = useState('');
  const [filas, setFilas] = useState([emptyFila()]);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const canSubmit = useMemo(() => {
    return !!user && !!nombreCam.trim() && !!fecIni && !!fecFin;
  }, [user, nombreCam, fecIni, fecFin]);

  const addFila = () => setFilas((prev) => [...prev, emptyFila()]);

  const removeFila = (idx) => {
    setFilas((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const updateFila = (idx, patch) => {
    setFilas((prev) => prev.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  };

  const addDesglose = (filaIdx) => {
    setFilas((prev) =>
      prev.map((f, i) =>
        i === filaIdx ? { ...f, desglose: [...f.desglose, { categoria: '', cantidad: '' }] } : f,
      ),
    );
  };

  const updateDesglose = (filaIdx, dIdx, patch) => {
    setFilas((prev) =>
      prev.map((f, i) => {
        if (i !== filaIdx) return f;
        const next = f.desglose.map((d, j) => (j === dIdx ? { ...d, ...patch } : d));
        return { ...f, desglose: next };
      }),
    );
  };

  const removeDesglose = (filaIdx, dIdx) => {
    setFilas((prev) =>
      prev.map((f, i) => {
        if (i !== filaIdx) return f;
        if (f.desglose.length <= 1) return f;
        return { ...f, desglose: f.desglose.filter((_, j) => j !== dIdx) };
      }),
    );
  };

  const buildPayloadFilas = () => {
    return filas.map((f, orden) => {
      const desglose = (f.desglose || [])
        .map((d) => ({
          categoria: String(d.categoria || '').trim(),
          cantidad: d.cantidad === '' || d.cantidad === null ? 0 : Number(d.cantidad),
        }))
        .filter((d) => d.categoria || d.cantidad > 0);
      return {
        tipo_penetracion: f.tipo_penetracion.trim(),
        orden,
        accion_visita: f.accion_visita || null,
        accion_contacto: f.accion_contacto || null,
        accion_oferta: f.accion_oferta || null,
        oferta_detalle: f.oferta_detalle || null,
        accion_mail: f.accion_mail || null,
        desglose,
      };
    });
  };

  const guardar = async () => {
    if (!canSubmit) {
      window.alert('Completa campaña y fechas.');
      return;
    }
    const payloadFilas = buildPayloadFilas();
    if (payloadFilas.some((f) => !f.tipo_penetracion)) {
      window.alert('Cada fila debe tener tipo de penetración.');
      return;
    }
    try {
      setLoading(true);
      setLastResult(null);
      const resp = await guardarAccionesCampania(user, {
        op: 'save',
        nombre_cam: nombreCam.trim(),
        fec_ini: fecIni,
        fec_fin: fecFin,
        filas: payloadFilas,
      });
      setLastResult(resp);
      if (resp?.ok) window.alert('Configuración guardada.');
      else window.alert(resp?.error || 'No se pudo guardar.');
    } catch (e) {
      console.error(e);
      window.alert(e?.message || 'Error al guardar.');
    } finally {
      setLoading(false);
    }
  };

  const consultar = async () => {
    if (!canSubmit) {
      window.alert('Completa campaña y fechas para consultar.');
      return;
    }
    try {
      setLoading(true);
      setLastResult(null);
      const resp = await consultarAccionesCampania(user, {
        nombre_cam: nombreCam.trim(),
        fec_ini: fecIni,
        fec_fin: fecFin,
      });
      setLastResult(resp);
      if (!resp?.ok) {
        window.alert(resp?.error || 'No encontrado.');
        return;
      }
      const list = Array.isArray(resp.filas) ? resp.filas : [];
      if (list.length === 0) {
        setFilas([emptyFila()]);
        return;
      }
      setFilas(
        list.map((f) => ({
          tipo_penetracion: f.tipo_penetracion || '',
          accion_visita: f.accion_visita || '',
          accion_contacto: f.accion_contacto || '',
          accion_oferta: f.accion_oferta || '',
          oferta_detalle: f.oferta_detalle || '',
          accion_mail: f.accion_mail || '',
          desglose:
            Array.isArray(f.desglose) && f.desglose.length > 0
              ? f.desglose.map((d) => ({ categoria: d.categoria || '', cantidad: d.cantidad ?? '' }))
              : [{ categoria: '', cantidad: '' }],
        })),
      );
      window.alert('Datos cargados en el formulario.');
    } catch (e) {
      console.error(e);
      window.alert(e?.message || 'Error al consultar.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-800">Acciones campaña</h1>
        <p className="text-gray-600">Matriz y desglose → MGC.acciones_*</p>
      </div>

      <div className="seccion-datos space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre campaña</label>
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={nombreCam}
              onChange={(e) => setNombreCam(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={fecIni}
              onChange={(e) => setFecIni(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={fecFin}
              onChange={(e) => setFecFin(e.target.value)}
            />
          </div>
        </div>

        <p className="text-sm text-gray-600">
          Usuario: <span className="font-medium text-gray-800">{user?.nombre || '—'}</span>
        </p>

        <div className="space-y-6">
          {filas.map((fila, fi) => (
            <div key={fi} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-semibold text-gray-700">Fila {fi + 1}</span>
                <button type="button" className="text-sm text-red-600 hover:underline" onClick={() => removeFila(fi)}>
                  Quitar fila
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tipo penetración</label>
                  <input
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    value={fila.tipo_penetracion}
                    onChange={(e) => updateFila(fi, { tipo_penetracion: e.target.value })}
                    placeholder="Ej. Fiel, Buena…"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Visita</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[72px]"
                    value={fila.accion_visita}
                    onChange={(e) => updateFila(fi, { accion_visita: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Contacto</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[72px]"
                    value={fila.accion_contacto}
                    onChange={(e) => updateFila(fi, { accion_contacto: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Oferta (texto acción)</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[72px]"
                    value={fila.accion_oferta}
                    onChange={(e) => updateFila(fi, { accion_oferta: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Detalle oferta (MGC)</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[72px]"
                    value={fila.oferta_detalle}
                    onChange={(e) => updateFila(fi, { oferta_detalle: e.target.value })}
                    placeholder="Obligatorio si hay texto en Oferta"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Mail</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm min-h-[72px]"
                    value={fila.accion_mail}
                    onChange={(e) => updateFila(fi, { accion_mail: e.target.value })}
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 pt-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-semibold text-gray-600">Desglose (categoría / cantidad)</span>
                  <button type="button" className="text-xs text-teal-700 hover:underline" onClick={() => addDesglose(fi)}>
                    + línea
                  </button>
                </div>
                <p className="text-xs text-amber-700 mb-2">
                  &quot;Clientes sin operaciones&quot; no está habilitada en backend.
                </p>
                <div className="space-y-2">
                  {fila.desglose.map((d, di) => (
                    <div key={di} className="flex flex-wrap gap-2 items-end">
                      <div className="flex-1 min-w-[140px]">
                        <input
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                          placeholder="Categoría"
                          value={d.categoria}
                          onChange={(e) => updateDesglose(fi, di, { categoria: e.target.value })}
                        />
                      </div>
                      <div className="w-28">
                        <input
                          type="number"
                          min={0}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                          placeholder="Cant."
                          value={d.cantidad}
                          onChange={(e) => updateDesglose(fi, di, { cantidad: e.target.value })}
                        />
                      </div>
                      <button
                        type="button"
                        className="text-xs text-gray-500 hover:text-red-600 pb-1"
                        onClick={() => removeDesglose(fi, di)}
                      >
                        Quitar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary text-sm" onClick={addFila} disabled={loading}>
            + Agregar fila
          </button>
          <button type="button" className="btn-primary" disabled={!canSubmit || loading} onClick={consultar}>
            {loading ? '…' : 'Consultar'}
          </button>
          <button type="button" className="btn-primary" disabled={!canSubmit || loading} onClick={guardar}>
            {loading ? 'Guardando…' : 'Guardar'}
          </button>
        </div>

        {lastResult && (
          <pre className="mt-4 text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-80">
            {JSON.stringify(lastResult, null, 2)}
          </pre>
        )}

        <p className="text-xs text-gray-500 max-w-3xl">
          Endpoint <code className="text-gray-700">POST /marketing/acciones-campania</code>. La Lambda valida solapes de
          fechas por campaña y exige detalle de oferta si hay acción de oferta.
        </p>
      </div>
    </div>
  );
};

export default AccionesCampana;
